import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { TimePicker } from '@paulownia/shared/components/TimePicker';
import { ProductSelectionModal } from '@paulownia/shared/components/ProductSelectionModal';
import { createOrder } from '@paulownia/shared/services/orders';
import { listProducts } from '@paulownia/shared/services/products';
import { getTenantById } from '@paulownia/shared/services/tenant';
import { getTenantMarketsWithDetails } from '@paulownia/shared/services/markets';
import { reportError } from '@paulownia/shared/lib/error-reporting';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { type Product, type OrderLineOption, type Market, type TenantMarket, type DayOfWeek, calculateOrderTotal, getOptionLabels, getPortionSize } from '@paulownia/shared/types';
import { useTenant } from '@paulownia/shared/lib/tenant-context';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';

interface LineForm {
  productId: string;
  productName: string;
  quantity: number;
  options: OrderLineOption[];
  portionSizeId?: string;
}

export function AddOrderSheet() {
  const { currentTenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [customerName, setCustomerName] = useState('');
  // No default time - user must select one
  const [pickupTime, setPickupTime] = useState('');

  // Date par défaut : depuis l'URL ou date du jour
  const [pickupDate, setPickupDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      return dateParam;
    }
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [lines, setLines] = useState<LineForm[]>([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [tenantSettings, setTenantSettings] = useState<{
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
  } | null>(null);
  const [tenantMarkets, setTenantMarkets] = useState<(TenantMarket & { market: Market })[]>([]);
  const [selectedMarketId, setSelectedMarketId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!currentTenantId) {
      setProducts([]);
      setTenantSettings(null);
      return;
    }
    listProducts(currentTenantId)
      .then((ps) => {
        setProducts(ps);
        if (ps.length === 0) {
          toast.info('Aucun produit disponible. Ajoutez des produits dans l\'onglet "Produits".');
        }
      })
      .catch((err) => {
        console.error('Error loading products:', err);
        setProducts([]);
        toast.error('Erreur lors du chargement des produits');
      });

    // Load tenant settings for section/subSection ordering
    getTenantById(currentTenantId)
      .then((tenant) => {
        if (tenant) {
          setTenantSettings(tenant.settings || null);
        }
      })
      .catch((error) => {
        reportError(error, { context: 'AddOrderSheet.loadTenantSettings', tenantId: currentTenantId });
      });
  }, [currentTenantId]);

  // Load tenant markets
  useEffect(() => {
    if (!currentTenantId) {
      setTenantMarkets([]);
      return;
    }
    getTenantMarketsWithDetails(currentTenantId)
      .then(setTenantMarkets)
      .catch((error) => {
        reportError(error, { context: 'AddOrderSheet.loadTenantMarkets', tenantId: currentTenantId });
        setTenantMarkets([]);
      });
  }, [currentTenantId]);

  // Resolve marketId from pickup date's day of week
  const marketsForPickupDay = (() => {
    if (!pickupDate || tenantMarkets.length === 0) return [];
    const [year, month, day] = pickupDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    // JS getDay(): 0=Sun, convert to ISO: 1=Mon...7=Sun
    const jsDay = date.getDay();
    const isoDay = (jsDay === 0 ? 7 : jsDay) as DayOfWeek;
    return tenantMarkets.filter((tm) => tm.market.dayOfWeek === isoDay);
  })();

  // Auto-select market when there's exactly one match
  useEffect(() => {
    if (marketsForPickupDay.length === 1) {
      setSelectedMarketId(marketsForPickupDay[0].marketId);
    } else if (marketsForPickupDay.length === 0) {
      setSelectedMarketId(undefined);
    } else if (!marketsForPickupDay.find(m => m.marketId === selectedMarketId)) {
      setSelectedMarketId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate, tenantMarkets]);
  const optionsEqual = (opts1: OrderLineOption[], opts2: OrderLineOption[]): boolean => {
    if (opts1.length !== opts2.length) return false;
    const sorted1 = [...opts1].sort((a, b) => a.id.localeCompare(b.id));
    const sorted2 = [...opts2].sort((a, b) => a.id.localeCompare(b.id));
    return sorted1.every((opt, i) =>
      opt.id === sorted2[i].id && opt.value === sorted2[i].value
    );
  };

  const handleAddProduct = (selection: {
    productId: string;
    productName: string;
    quantity: number;
    options: OrderLineOption[];
    portionSizeId?: string;
  }) => {
    setLines((prev) => {
      // Chercher une ligne existante avec le même produit, les mêmes options ET le même portionSizeId
      const existingIndex = prev.findIndex(
        (line) =>
          line.productId === selection.productId &&
          line.portionSizeId === selection.portionSizeId &&
          optionsEqual(line.options, selection.options)
      );

      // Si le produit existe déjà avec les mêmes options, incrémenter la quantité
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + selection.quantity,
        };
        return updated;
      }

      // Sinon, ajouter une nouvelle ligne
      return [...prev, selection];
    });
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      if (!customerName.trim()) throw new Error('Nom client requis');
      if (!pickupTime || !pickupTime.includes(':')) throw new Error('Veuillez sélectionner une heure de récupération');
      if (lines.length === 0) throw new Error('Ajoutez au moins un produit');
      if (!currentTenantId) throw new Error('Tenant non sélectionné');

      // Utiliser la date choisie (ou celle du jour si non modifiée)
      const [h, m] = pickupTime.split(':').map((n) => Number(n));
      const [year, month, day] = pickupDate.split('-').map((n) => Number(n));
      const at = new Date(year, month - 1, day, h, m, 0, 0);

      // Optimistic creation: fire and forget
      createOrder({
        customerName: customerName.trim(),
        pickupAtISO: at.toISOString(),
        lines: lines.map((l) => ({
          productId: l.productId,
          quantity: l.quantity,
          preparedQuantity: 0,
          options: l.options,
          portionSizeId: l.portionSizeId,
        })),
        note: note.trim() || undefined,
        tenantId: currentTenantId,
        marketId: selectedMarketId,
      }).catch((e) => {
        console.error('Erreur création background:', e);
        toast.error('Erreur lors de la synchro création: ' + e.message);
      });

      toast.success('Commande créée');
      navigate('/dashboard');
      // No need to setSubmitting(false) as we navigate away
    } catch (e) {
      console.error('Erreur preparation commande:', e);
      const error = e as Error;
      toast.error(error?.message || 'Erreur lors de la préparation');
      setSubmitting(false);
    }
  };

  const total = calculateOrderTotal(
    lines.map(l => ({
      productId: l.productId,
      quantity: l.quantity,
      preparedQuantity: 0,
      options: l.options,
      portionSizeId: l.portionSizeId,
    })),
    products
  );

  return (
    <>
      <div className="pb-16 p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold">Nouvelle commande</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>Annuler</Button>
            <Button
              onClick={onSubmit}
              disabled={submitting || lines.length === 0 || !pickupTime}
            >
              {submitting ? 'Création...' : 'Créer la commande'}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {total !== null && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-900">Total TTC</span>
                <span className="text-xl font-bold text-green-700">{total.toFixed(2)} €</span>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="customer-name">Nom du client</Label>
            <Input
              id="customer-name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Nom du client"
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label>Date de récupération</Label>
            <Input
              type="date"
              value={pickupDate}
              onChange={(e) => setPickupDate(e.target.value)}
              className="h-11 w-full text-base appearance-none"
              style={{ fontSize: '16px' }}
            />
          </div>

          <TimePicker
            label="Heure de récupération"
            value={pickupTime}
            onChange={setPickupTime}
          />

          {marketsForPickupDay.length > 1 && (
            <div className="space-y-2">
              <Label>Marché</Label>
              <Select value={selectedMarketId ?? ''} onValueChange={setSelectedMarketId}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Sélectionner un marché" />
                </SelectTrigger>
                <SelectContent>
                  {marketsForPickupDay.map((tm) => (
                    <SelectItem key={tm.marketId} value={tm.marketId}>
                      {tm.market.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-3" data-testid="products-section">
            <div className="flex items-center justify-between">
              <div className="font-medium">Produits</div>
              <span className="text-sm text-gray-500">{lines.length} produit{lines.length > 1 ? 's' : ''}</span>
            </div>

            {lines.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                <p className="text-gray-500 mb-4">Aucun produit ajouté</p>
                <Button
                  onClick={() => setModalOpen(true)}
                  data-testid="add-product-button"
                >
                  Ajouter un produit
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {lines.map((line, index) => {
                    const product = products.find(p => p.id === line.productId);
                    const optionLabels = product ? getOptionLabels(
                      { productId: line.productId, quantity: line.quantity, preparedQuantity: 0, options: line.options },
                      product
                    ) : [];
                    const portionSize = product ? getPortionSize(product, line.portionSizeId) : undefined;

                    // Format quantity display based on portion size
                    const quantityDisplay = portionSize
                      ? `${line.quantity}× ${portionSize.label}`
                      : `${line.quantity}×`;

                    return (
                      <div key={index} className="rounded-md border bg-white p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg">{quantityDisplay}</span>
                              <span className="font-medium">{line.productName}</span>
                            </div>
                            {optionLabels.length > 0 && (
                              <div className="flex gap-2 mt-2">
                                {optionLabels.map((label, idx) => (
                                  <Badge key={idx} variant="secondary">{label}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLine(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 w-9"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  onClick={() => setModalOpen(true)}
                  className="w-full"
                  data-testid="add-product-button"
                >
                  + Ajouter un produit
                </Button>
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label>Note (optionnel)</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ajouter une note personnalisée..."
              className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md text-base resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

        </div>
      </div>

      <ProductSelectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        products={products}
        onConfirm={handleAddProduct}
        tenantSettings={tenantSettings}
      />
    </>
  );
}


